import type { DbClient } from "@tanstack/db";
import type { QueryClient } from "@tanstack/query-core";
import { requestUnavailable } from "@softmax/observatory-client/transport";
import { replayLaunchCollection } from "./replay-session.collections";
import {
  replaySessionResultSchema,
  type ReplayLaunchIdentity,
  type ReplaySessionResult,
} from "./replay-session-records";
import { createReplaySession } from "./create-replay-session";

export async function discardUnavailableReplayLaunch({
  db,
  identity,
  error,
}: Readonly<{
  db: DbClient;
  identity: ReplayLaunchIdentity;
  error: Error;
}>): Promise<void> {
  if (!requestUnavailable(error)) return;

  db.requireDependency<AbortController>("lifetime").signal.throwIfAborted();
  await db
    .collection(replayLaunchCollection(identity))
    .delete("launch")
    .when("settled");
}

export async function launchReplaySession({
  db,
  identity,
  signal,
}: Readonly<{
  db: DbClient;
  identity: ReplayLaunchIdentity;
  signal: AbortSignal;
}>): Promise<void> {
  const lifetimeSignal =
    db.requireDependency<AbortController>("lifetime").signal;
  signal.throwIfAborted();
  lifetimeSignal.throwIfAborted();
  const client = db.requireDependency<QueryClient>("queryClient");
  const mutationKey = [
    "coworld-replay-launch",
    identity.coworldId,
    identity.replayUri,
    identity.elevatedPrivileges,
  ];
  // Only the viewer scope cancels a shared POST; closing one frame cancels its own receipt.
  const pending = client
    .getMutationCache()
    .find<ReplaySessionResult>({ mutationKey, status: "pending" });
  const launch =
    pending ??
    client
      .getMutationCache()
      .build<ReplaySessionResult, Error, void, unknown>(client, {
        mutationKey,
        networkMode: "always",
        retry: false,
        mutationFn: () =>
          createReplaySession({ ...identity, signal: lifetimeSignal }),
      });
  const result = pending
    ? replaySessionResultSchema.parse(await pending.continue())
    : await launch.execute(undefined);
  signal.throwIfAborted();
  lifetimeSignal.throwIfAborted();
  await db
    .collection(replayLaunchCollection(identity))
    .insert({ id: "launch", ...result, sessionShared: pending !== undefined })
    .when("settled");
}
